<script setup lang="ts">
/**
 * SalesNowCard — "ยอดขายตอนนี้" quick-pulse hero card (PO mockup 2026-09-01).
 *
 * The homepage's opening card: one big sales number with a LOCAL period
 * toggle (วันล่าสุด / สัปดาห์นี้ / เดือนนี้), an inline branch selector,
 * the Suki/BBQ split rows, and a trailing-7-day mini bar chart. Every
 * control is scoped to this card only — it never touches the route lens
 * or other cards (mockup note: ตัวเลือกในการ์ดนี้มีผลเฉพาะการ์ดนี้).
 *
 * Single source for hero/mix: GET /sales/trend (days + per-day sales_mix,
 * supports brand + branch_id) — the same canonical read behind the pulse/WoW
 * cards, so the hero can never disagree with them. The month view's
 * whole-year chart reads GET /historical?mode=custom&bucket=month (purpose-
 * built monthly buckets with has_data/partial flags; /sales/trend is capped
 * at 90 days server-side and cannot span a year). Branch list comes from
 * GET /branches (loaded once on first open, like BranchSearch).
 *
 * Chart matches the selected period (PO 2026-09-01):
 *  - วันล่าสุด  → latest data day + the 7 days before it (D-7..D) — never
 *    renders future days where no data can exist;
 *  - สัปดาห์นี้ → current ISO week + 4 previous weeks (5 weekly bars);
 *  - เดือนนี้   → the whole calendar year of the latest data day (12 bars).
 * Bar values are plain millions (ตัวเลขบนแท่ง = ล้านบาท) so 12 columns fit
 * on mobile; past gaps render as small dashed stubs, never full-height.
 * The header ย้อนหลัง button opens a custom-range dialog — it collects BOTH
 * start and end dates (validated: ordered, non-future, ≤366 days) before
 * building the /historical deep link (mode=custom, span-appropriate bucket,
 * carrying the card's lens + branch).
 *
 * Honesty rules (repo discipline):
 *  - windows are anchored on the LATEST DATA DAY, not wall-clock today
 *    (POS uploads lag); every sub-line names its exact date window;
 *  - week = trailing 7 ICT calendar days ending at the latest data day,
 *    with missing days rendered as explicit empty bars (never zero-filled);
 *  - month = the latest data day's ICT month-to-date; when that month is
 *    not the current ICT month the sub-line says เดือนล่าสุดที่มีข้อมูล;
 *  - hero is base sales — labeled ไม่รวม VAT (sales convention, §5);
 *  - brand-lens requests hide the split rows (sales_mix is one brand only);
 *    unattributed net, when present, is captioned rather than folded in.
 *
 * bead: lucky-war-e0yv · v2 charts + date picker: lucky-war-zmwz
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { BranchSummary, HistoricalBrandCode, HistoricalSalesSeries, SalesTrendResponse } from '@/api';
import { fetchBranches, fetchHistorical, fetchSalesTrend } from '@/api';
import { useAsyncData } from '@/composables/useAsyncData';
import { apiBrand, lensFromQuery, scopeLabel } from '@/lib/brand';
import { HISTORICAL_RANGE_MAX_DAYS } from '@/lib/historicalRange';
import ShareButton from '@/components/ShareButton.vue';
import type { KpiCardShareInput, KpiRow } from '@/lib/shareFlex';

type Period = 'day' | 'week' | 'month';

const route = useRoute();
const router = useRouter();
const lens = computed(() => lensFromQuery(route.query.brand));

// ── Card-local state (never leaves this card) ────────────────────────────────
const period = ref<Period>('day');
const branchId = ref<string | null>(null);

const PERIODS: { value: Period; label: string }[] = [
  { value: 'day', label: 'วันล่าสุด' },
  { value: 'week', label: 'สัปดาห์นี้' },
  { value: 'month', label: 'เดือนนี้' },
];

// ── Data: one trend fetch covers all three periods + the bars ────────────────
// 62 days is enough for any month window (31) + the trailing-7 bars.
const trendState = useAsyncData<SalesTrendResponse>(
  (signal) => fetchSalesTrend(62, signal, apiBrand(lens.value), branchId.value ?? undefined),
);
const { data, loading, error, reload } = trendState;
const days = computed(() => data.value?.days ?? []);
const latest = computed(() => (days.value.length ? days.value[days.value.length - 1] : null));

watch(() => route.query.brand, () => {
  // Lens change can orphan the selected branch (brand mismatch) — reset.
  branchId.value = null;
  yearStale = true;
  reload();
  maybeLoadYear();
});
watch(branchId, () => {
  yearStale = true;
  reload();
  maybeLoadYear();
});

// ── Date helpers (UTC-stable — avoid ICT off-by-one, cf. ChainWowCard) ───────
const THAI_SHORT_MONTHS = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
];
const THAI_WEEKDAYS_LONG = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];
const THAI_WEEKDAYS_SHORT = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

function isoToDate(iso: string): Date {
  const p = iso.split('-').map(Number);
  return new Date(Date.UTC(p[0], p[1] - 1, p[2]));
}

function dateToIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function fmtDayMonth(iso: string): string {
  const d = isoToDate(iso);
  return `${d.getUTCDate()} ${THAI_SHORT_MONTHS[d.getUTCMonth()]}`;
}

function ictTodayIso(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Bangkok' }).format(new Date());
}

/** "22–28 ส.ค." — each endpoint carries its own month like ChainWowCard. */
function windowLabel(isos: string[]): string {
  if (isos.length === 0) return '';
  const sorted = [...isos].sort();
  if (sorted.length === 1) return fmtDayMonth(sorted[0]);
  return `${fmtDayMonth(sorted[0])}–${fmtDayMonth(sorted[sorted.length - 1])}`;
}

// ── Period windows (anchored on the latest DATA day, not wall clock) ─────────
// Week = the ISO week (Mon–Sun, จ..อา) containing the latest data day — same
// shape as the PO mockup: days beyond the data frontier render as explicit
// empty bars, never zero-filled.
const weekDates = computed<string[]>(() => {
  if (!latest.value) return [];
  const end = isoToDate(latest.value.date);
  const mondayOffset = (end.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  const monday = new Date(end);
  monday.setUTCDate(monday.getUTCDate() - mondayOffset);
  const out: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setUTCDate(d.getUTCDate() + i);
    out.push(dateToIso(d));
  }
  return out;
});

const monthDates = computed<string[]>(() => {
  if (!latest.value) return [];
  const prefix = latest.value.date.slice(0, 7);
  return days.value.filter((d) => d.date.startsWith(prefix)).map((d) => d.date);
});

const periodDates = computed<string[]>(() => {
  switch (period.value) {
    case 'day': return latest.value ? [latest.value.date] : [];
    case 'week': return weekDates.value;
    case 'month': return monthDates.value;
  }
});

/** Days that actually have rows within the selected window. */
const periodDataDays = computed(() => {
  const inWindow = new Set(periodDates.value);
  return days.value.filter((d) => inWindow.has(d.date));
});

const heroTotal = computed(() => periodDataDays.value.reduce((s, d) => s + d.net_total, 0));

// ── Brand split (chain lens only — a brand lens makes one row ~100%) ─────────
const mixTotals = computed(() => {
  let suki = 0;
  let bbq = 0;
  let unattributed = 0;
  for (const d of periodDataDays.value) {
    if (!d.sales_mix) continue;
    suki += d.sales_mix.suki_net_total;
    bbq += d.sales_mix.bbq_net_total;
    unattributed += d.sales_mix.unattributed_net_total;
  }
  return { suki, bbq, unattributed };
});
const showSplit = computed(() => lens.value === 'all');

// ── Sub-line (names the exact window — honesty discipline) ───────────────────
const subLabel = computed(() => {
  if (!latest.value) return '';
  const vat = 'ไม่รวม VAT';
  if (period.value === 'day') {
    const d = isoToDate(latest.value.date);
    return `ยอดขายวัน${THAI_WEEKDAYS_LONG[d.getUTCDay()]} ${fmtDayMonth(latest.value.date)} · วันล่าสุดที่มีข้อมูล · ${vat}`;
  }
  if (period.value === 'week') {
    const coverage = `มีข้อมูล ${periodDataDays.value.length}/7 วัน`;
    return `ยอดขายสัปดาห์ ${windowLabel(weekDates.value)} · ${coverage} · ${vat}`;
  }
  const isCurrentMonth = latest.value.date.slice(0, 7) === ictTodayIso().slice(0, 7);
  const scope = isCurrentMonth ? 'เดือนนี้' : 'เดือนล่าสุดที่มีข้อมูล';
  return `ยอดขาย ${windowLabel(monthDates.value)} · ${scope} · ${vat}`;
});

// ── Money formatting ─────────────────────────────────────────────────────────
function fmtCompactBaht(v: number): string {
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) >= 1e6) return `฿${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `฿${Math.round(v / 1e3)}k`;
  return `฿${Math.round(v)}`;
}

// ── Period-matched chart (PO 2026-09-01) ─────────────────────────────────────
interface ChartBar {
  key: string;
  label: string;
  net: number;
  valueLabel: string;
  missing: boolean;
  highlight: boolean;
  heightPx: number;
}

const BAR_AREA_PX = 56;

/** Plain millions ("9.0", "271") — no ฿ sign, so 12 columns fit on mobile. */
function fmtMillions(v: number): string {
  const m = v / 1e6;
  return m >= 100 ? String(Math.round(m)) : m.toFixed(1);
}

/** Past gaps render as small dashed stubs — never full-height empties. */
function withHeights(bars: ChartBar[]): ChartBar[] {
  const max = Math.max(0, ...bars.map((b) => b.net));
  return bars.map((b) => ({
    ...b,
    heightPx: b.net <= 0 || max <= 0 ? 6 : Math.max(6, Math.round((b.net / max) * BAR_AREA_PX)),
  }));
}

// วันล่าสุด → D-7..D (8 daily bars). Anchored on the latest DATA day, so no
// future column can ever appear.
const dayChart = computed<ChartBar[]>(() => {
  if (!latest.value) return [];
  const end = isoToDate(latest.value.date);
  const byDate = new Map(days.value.map((d) => [d.date, d]));
  const bars: ChartBar[] = [];
  for (let i = 7; i >= 0; i--) {
    const d = new Date(end);
    d.setUTCDate(d.getUTCDate() - i);
    const iso = dateToIso(d);
    const row = byDate.get(iso);
    bars.push({
      key: iso,
      label: THAI_WEEKDAYS_SHORT[d.getUTCDay()],
      net: row?.net_total ?? 0,
      valueLabel: row ? fmtMillions(row.net_total) : '—',
      missing: !row,
      highlight: iso === latest.value!.date,
      heightPx: 0,
    });
  }
  return withHeights(bars);
});

// สัปดาห์นี้ → the current ISO week + 4 previous weeks (5 weekly totals).
// 62 trend days always cover Monday(D) − 34 days.
const weekChart = computed<ChartBar[]>(() => {
  if (!latest.value) return [];
  const end = isoToDate(latest.value.date);
  const mondayOffset = (end.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  const curMonday = new Date(end);
  curMonday.setUTCDate(curMonday.getUTCDate() - mondayOffset);
  const byDate = new Map(days.value.map((d) => [d.date, d]));
  const bars: ChartBar[] = [];
  for (let w = 4; w >= 0; w--) {
    const monday = new Date(curMonday);
    monday.setUTCDate(monday.getUTCDate() - 7 * w);
    let net = 0;
    let dataDays = 0;
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setUTCDate(d.getUTCDate() + i);
      const row = byDate.get(dateToIso(d));
      if (row) {
        net += row.net_total;
        dataDays++;
      }
    }
    const mondayIso = dateToIso(monday);
    bars.push({
      key: mondayIso,
      label: fmtDayMonth(mondayIso),
      net,
      valueLabel: dataDays > 0 ? fmtMillions(net) : '—',
      missing: dataDays === 0,
      highlight: w === 0,
      heightPx: 0,
    });
  }
  return withHeights(bars);
});

// เดือนนี้ → the whole calendar year of the latest data day (12 monthly
// buckets from /historical). Lazy: fetched only when the month view opens.
const yearData = ref<HistoricalSalesSeries[] | null>(null);
const yearLoading = ref(false);
const yearError = ref(false);
let yearStale = true;
let yearController: AbortController | null = null;

async function loadYear() {
  if (!latest.value) return;
  const year = latest.value.date.slice(0, 4);
  const dec31 = `${year}-12-31`;
  const to = dec31 < ictTodayIso() ? dec31 : ictTodayIso(); // custom mode rejects future
  yearController?.abort();
  const controller = new AbortController();
  yearController = controller;
  yearLoading.value = true;
  yearError.value = false;
  try {
    const res = await fetchHistorical({
      mode: 'custom',
      from: `${year}-01-01`,
      to,
      bucket: 'month',
      // apiBrand() only ever returns 'lucky_suki' | 'lucky_bbq' | undefined.
      brand: apiBrand(lens.value) as HistoricalBrandCode | undefined,
      branch: branchId.value ?? undefined,
    }, controller.signal);
    if (!controller.signal.aborted) {
      yearData.value = res.series.sales;
      yearStale = false;
    }
  } catch {
    if (!controller.signal.aborted) yearError.value = true;
  } finally {
    if (!controller.signal.aborted) yearLoading.value = false;
  }
}

function maybeLoadYear() {
  if (period.value !== 'month' || !latest.value) return;
  if (yearLoading.value || (!yearStale && yearData.value)) return;
  loadYear();
}

watch(period, maybeLoadYear, { immediate: true });
watch(latest, maybeLoadYear);

const monthChart = computed<ChartBar[]>(() => {
  if (!latest.value || !yearData.value) return [];
  const year = latest.value.date.slice(0, 4);
  const highlightMonth = latest.value.date.slice(5, 7);
  const byMonth = new Map(yearData.value.map((s) => [s.from.slice(5, 7), s]));
  const bars: ChartBar[] = [];
  for (let m = 1; m <= 12; m++) {
    const mm = String(m).padStart(2, '0');
    const s = byMonth.get(mm);
    const has = !!s && s.has_data && s.net_total != null;
    bars.push({
      key: `${year}-${mm}`,
      label: THAI_SHORT_MONTHS[m - 1],
      net: s?.net_total ?? 0,
      valueLabel: has ? fmtMillions(s!.net_total!) : '—',
      missing: !has,
      highlight: mm === highlightMonth,
      heightPx: 0,
    });
  }
  return withHeights(bars);
});

const chart = computed<ChartBar[]>(() => {
  switch (period.value) {
    case 'day': return dayChart.value;
    case 'week': return weekChart.value;
    case 'month': return monthChart.value;
  }
});

const chartAria = computed(() => {
  switch (period.value) {
    case 'day': return 'ยอดขาย 8 วันล่าสุด (ล้านบาท)';
    case 'week': return 'ยอดขายรายสัปดาห์ 5 สัปดาห์ล่าสุด (ล้านบาท)';
    case 'month': return `ยอดขายรายเดือน ปี ${latest.value?.date.slice(0, 4) ?? ''} (ล้านบาท)`;
  }
});

const chartUnavailableMsg = computed(() => {
  if (period.value !== 'month' || chart.value.length > 0) return '';
  if (yearLoading.value) return 'กำลังโหลดกราฟรายเดือน...';
  return 'ไม่สามารถโหลดกราฟรายเดือนได้';
});

// ── Custom range dialog → /historical deep link ──────────────────────────────
const rangeOpen = ref(false);
const rangeFrom = ref('');
const rangeTo = ref('');
const rangeError = ref('');
const rangeFromInput = ref<HTMLInputElement | null>(null);
const rangeTrigger = ref<HTMLButtonElement | null>(null);
const todayIso = ictTodayIso();

const rangeFromMax = computed(() => (rangeTo.value && rangeTo.value < todayIso ? rangeTo.value : todayIso));

function openRangeDialog() {
  rangeFrom.value = '';
  rangeTo.value = '';
  rangeError.value = '';
  rangeOpen.value = true;
  nextTick(() => rangeFromInput.value?.focus());
}

function closeRangeDialog() {
  rangeOpen.value = false;
  rangeError.value = '';
  rangeTrigger.value?.focus();
}

/** Span-appropriate /historical bucket (the page lets the user change it). */
function rangeBucket(from: string, to: string): 'day' | 'week' | 'month' {
  const days = Math.round((isoToDate(to).getTime() - isoToDate(from).getTime()) / 86_400_000) + 1;
  if (days <= 45) return 'day';
  if (days <= 140) return 'week';
  return 'month';
}

/**
 * Validates start + end, then deep-links the historical dashboard for the
 * custom range, carrying the card's current lens + branch. Mirrors the
 * server's own rules (resolveHistoricalRange): from ≤ to, no future end,
 * span ≤ HISTORICAL_RANGE_MAX_DAYS.
 */
function submitRange() {
  const from = rangeFrom.value;
  const to = rangeTo.value;
  if (!from || !to) {
    rangeError.value = 'กรุณาเลือกทั้งวันที่เริ่มต้นและวันที่สิ้นสุด';
    return;
  }
  if (from > to) {
    rangeError.value = 'วันที่สิ้นสุดต้องไม่ก่อนวันที่เริ่มต้น';
    return;
  }
  if (to > todayIso) {
    rangeError.value = 'ไม่สามารถเลือกวันที่ในอนาคตได้';
    return;
  }
  const days = Math.round((isoToDate(to).getTime() - isoToDate(from).getTime()) / 86_400_000) + 1;
  if (days > HISTORICAL_RANGE_MAX_DAYS) {
    rangeError.value = `ช่วงวันที่ยาวเกินไป (สูงสุด ${HISTORICAL_RANGE_MAX_DAYS} วัน)`;
    return;
  }
  const query: Record<string, string> = { mode: 'custom', from, to, bucket: rangeBucket(from, to) };
  const brand = apiBrand(lens.value);
  if (brand) query.brand = brand;
  if (branchId.value) query.branch = branchId.value;
  rangeOpen.value = false;
  router.push({ path: '/historical', query });
}

// ── Branch selector ──────────────────────────────────────────────────────────
const branchRoot = ref<HTMLElement | null>(null);
const branchOpen = ref(false);
const branchQuery = ref('');
const branchLoading = ref(false);
const allBranches = ref<BranchSummary[]>([]);

let branchController: AbortController | null = null;

async function loadBranches() {
  if (branchLoading.value || allBranches.value.length > 0) return;
  branchController?.abort();
  const controller = new AbortController();
  branchController = controller;
  branchLoading.value = true;
  try {
    const res = await fetchBranches({ perPage: 500 }, controller.signal);
    if (!controller.signal.aborted) allBranches.value = res.items;
  } catch {
    // Leave empty — next open retries.
  } finally {
    if (!controller.signal.aborted) branchLoading.value = false;
  }
}

/** Dropdown honors the page brand lens — picking a cross-brand branch would
 *  only yield an empty trend (brand + branch_id are ANDed server-side). */
const branchOptions = computed(() => {
  const brand = apiBrand(lens.value);
  const list = brand
    ? allBranches.value.filter((b) => b.brand?.code === brand)
    : allBranches.value;
  const q = branchQuery.value.trim().toLowerCase();
  if (!q) return list;
  return list.filter(
    (b) => b.name_th?.toLowerCase().includes(q) || b.address_th?.toLowerCase().includes(q),
  );
});

const selectedBranch = computed(() =>
  branchId.value ? allBranches.value.find((b) => b.id === branchId.value) ?? null : null,
);

const branchButtonLabel = computed(() => {
  if (selectedBranch.value) return `สาขา: ${selectedBranch.value.name_th}`;
  const n = allBranches.value.length;
  return n > 0 ? `สาขา: ทุกสาขา (${n})` : 'สาขา: ทุกสาขา';
});

function toggleBranch() {
  branchOpen.value = !branchOpen.value;
  if (branchOpen.value) {
    branchQuery.value = '';
    loadBranches();
  }
}

function closeBranch() {
  branchOpen.value = false;
}

function pickBranch(id: string | null) {
  branchId.value = id;
  closeBranch();
}

function onDocPointerDown(ev: PointerEvent) {
  if (!branchOpen.value) return;
  if (branchRoot.value && !branchRoot.value.contains(ev.target as Node)) closeBranch();
}

function onDocKeydown(ev: KeyboardEvent) {
  if (ev.key !== 'Escape') return;
  if (rangeOpen.value) closeRangeDialog();
  else if (branchOpen.value) closeBranch();
}

onMounted(() => {
  document.addEventListener('pointerdown', onDocPointerDown, true);
  document.addEventListener('keydown', onDocKeydown, true);
});
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocPointerDown, true);
  document.removeEventListener('keydown', onDocKeydown, true);
  branchController?.abort();
});

// ── Detail deep-link ─────────────────────────────────────────────────────────
const detailTarget = computed(() =>
  selectedBranch.value ? `/branches/${selectedBranch.value.id}` : '/discovery',
);
const detailLabel = computed(() =>
  selectedBranch.value ? 'ดูรายละเอียดสาขานี้ ›' : 'ดูรายละเอียดรายวัน ›',
);

const titleText = computed(() => `ยอดขายตอนนี้ · ${scopeLabel(lens.value)}`);

// ── LIFF share (bead lucky-war-trko) ─────────────────────────────────────────
// Flex payload from the card's CURRENT state (period toggle, branch selector,
// brand lens all apply). Built at click time, so it can never disagree with
// what the user sees.
function shareInput(): KpiCardShareInput {
  const periodLabel = PERIODS.find((p) => p.value === period.value)?.label ?? '';
  const rows: KpiRow[] = [{ label: 'ยอดขายรวม', value: fmtCompactBaht(heroTotal.value) }];
  if (showSplit.value) {
    rows.push({ label: 'สุกี้', value: fmtCompactBaht(mixTotals.value.suki) });
    rows.push({ label: 'บาร์บีคิว', value: fmtCompactBaht(mixTotals.value.bbq) });
  }
  return {
    title: periodLabel ? `ยอดขายตอนนี้ · ${periodLabel}` : 'ยอดขายตอนนี้',
    scope: selectedBranch.value ? selectedBranch.value.name_th : scopeLabel(lens.value),
    windowLabel: subLabel.value,
    rows,
    linkPath: route.fullPath,
  };
}
</script>

<template>
  <section class="snc" :aria-label="titleText">
    <div class="snc__headrow">
      <h2 class="card-head">{{ titleText }}</h2>
      <!-- Custom range → dialog → /historical deep link -->
      <button
        ref="rangeTrigger"
        type="button"
        class="snc__datepick-btn"
        aria-haspopup="dialog"
        @click="openRangeDialog"
      >ย้อนหลัง</button>
      <ShareButton :build="shareInput" />
    </div>

    <p v-if="loading" class="snc__state">กำลังโหลดข้อมูลยอดขาย...</p>
    <p v-else-if="error" class="snc__state">ไม่สามารถโหลดข้อมูลยอดขายได้</p>
    <p v-else-if="!days.length" class="snc__state">ยังไม่มีข้อมูลยอดขายในระบบ</p>

    <template v-else>
      <!-- Period toggle — scoped to this card only -->
      <div class="snc__periods" role="group" aria-label="ช่วงเวลาของการ์ดนี้">
        <button
          v-for="p in PERIODS"
          :key="p.value"
          type="button"
          class="snc__period"
          :class="{ 'snc__period--on': period === p.value }"
          :aria-pressed="period === p.value"
          @click="period = p.value"
        >
          {{ p.label }}
        </button>
      </div>

      <!-- Branch selector -->
      <div ref="branchRoot" class="snc__branch">
        <button
          type="button"
          class="snc__branch-btn"
          :aria-expanded="branchOpen"
          aria-haspopup="listbox"
          @click="toggleBranch"
        >
          <span class="snc__branch-label">{{ branchButtonLabel }}</span>
          <span class="snc__branch-caret" aria-hidden="true">⌄</span>
        </button>
        <div v-if="branchOpen" class="snc__branch-panel" role="listbox" aria-label="เลือกสาขา">
          <input
            v-model="branchQuery"
            class="snc__branch-search"
            type="search"
            placeholder="ค้นหาสาขา..."
            aria-label="ค้นหาสาขา"
          >
          <p v-if="branchLoading" class="snc__branch-note">กำลังโหลดรายการสาขา...</p>
          <p v-else-if="!branchOptions.length" class="snc__branch-note">ไม่พบสาขาที่ตรงกับการค้นหา</p>
          <template v-else>
            <button
              type="button"
              class="snc__branch-option"
              :class="{ 'snc__branch-option--on': branchId === null }"
              role="option"
              :aria-selected="branchId === null"
              @click="pickBranch(null)"
            >
              ทุกสาขา ({{ allBranches.length }})
            </button>
            <ul class="snc__branch-list">
              <li v-for="b in branchOptions" :key="b.id">
                <button
                  type="button"
                  class="snc__branch-option"
                  :class="{ 'snc__branch-option--on': branchId === b.id }"
                  role="option"
                  :aria-selected="branchId === b.id"
                  @click="pickBranch(b.id)"
                >
                  {{ b.name_th }}
                </button>
              </li>
            </ul>
          </template>
        </div>
      </div>

      <!-- Hero -->
      <div class="snc__hero">{{ fmtCompactBaht(heroTotal) }}</div>
      <div class="snc__sub">{{ subLabel }}</div>

      <!-- Brand split (chain lens only) -->
      <div v-if="showSplit" class="snc__split">
        <div class="snc__split-row snc__split-row--suki">
          <span class="snc__split-dot snc__split-dot--suki" aria-hidden="true" />
          <span class="snc__split-name">ลัคกี้สุกี้</span>
          <span class="snc__split-value">{{ fmtCompactBaht(mixTotals.suki) }}</span>
        </div>
        <div class="snc__split-row snc__split-row--bbq">
          <span class="snc__split-dot snc__split-dot--bbq" aria-hidden="true" />
          <span class="snc__split-name">ลัคกี้บาร์บีคิว</span>
          <span class="snc__split-value">{{ fmtCompactBaht(mixTotals.bbq) }}</span>
        </div>
        <p v-if="mixTotals.unattributed > 0" class="snc__split-note">
          มีอีก {{ fmtCompactBaht(mixTotals.unattributed) }} ที่ระบุสาขาไม่ได้ (ไม่นับในแถวด้านบน)
        </p>
      </div>

      <!-- Period-matched chart: day → D-7..D · week → 5 ISO weeks · month → 12 months -->
      <template v-if="chart.length">
        <div class="snc__bars" role="img" :aria-label="chartAria">
          <div v-for="bar in chart" :key="bar.key" class="snc__bar-col">
            <span class="snc__bar-value" :class="{ 'snc__bar-value--latest': bar.highlight }">
              {{ bar.valueLabel }}
            </span>
            <div
              class="snc__bar"
              :class="{ 'snc__bar--empty': bar.missing, 'snc__bar--latest': bar.highlight }"
              :style="{ height: `${bar.heightPx}px` }"
            />
            <span class="snc__bar-label">{{ bar.label }}</span>
          </div>
        </div>
        <p class="snc__bars-unit">ตัวเลขบนแท่ง = ล้านบาท · ไม่รวม VAT</p>
      </template>
      <p v-else-if="chartUnavailableMsg" class="snc__state snc__state--chart">{{ chartUnavailableMsg }}</p>

      <RouterLink :to="detailTarget" class="snc__detail">{{ detailLabel }}</RouterLink>
    </template>

    <p class="snc__foot">
      ตัวเลือกในการ์ดนี้มีผลเฉพาะการ์ดนี้ · ยอดขายจากไฟล์ POS (base sales)
    </p>

    <!-- Custom range dialog (EvidenceDrawer pattern: Teleport + backdrop +
         role=dialog + Esc). Collects start AND end before the /historical
         URL is built. -->
    <Teleport to="body">
      <div v-if="rangeOpen" class="snc__range-backdrop" @click.self="closeRangeDialog">
        <section class="snc__range" role="dialog" aria-modal="true" aria-labelledby="snc-range-title">
          <h3 id="snc-range-title" class="snc__range-title">เลือกช่วงวันที่ย้อนหลัง</h3>
          <div class="snc__range-fields">
            <label class="snc__range-field">
              <span class="snc__range-field-label">วันที่เริ่มต้น</span>
              <input
                ref="rangeFromInput"
                v-model="rangeFrom"
                class="snc__range-input"
                type="date"
                :max="rangeFromMax"
              >
            </label>
            <span class="snc__range-sep" aria-hidden="true">ถึง</span>
            <label class="snc__range-field">
              <span class="snc__range-field-label">วันที่สิ้นสุด</span>
              <input
                v-model="rangeTo"
                class="snc__range-input"
                type="date"
                :min="rangeFrom || undefined"
                :max="todayIso"
              >
            </label>
          </div>
          <p v-if="rangeError" class="snc__range-error" role="alert">{{ rangeError }}</p>
          <div class="snc__range-actions">
            <button type="button" class="snc__range-cancel" @click="closeRangeDialog">ยกเลิก</button>
            <button type="button" class="snc__range-go" @click="submitRange">ดูรายงาน</button>
          </div>
          <p class="snc__range-hint">จะเปิดหน้ารายงานย้อนหลังตามช่วงวันที่ที่เลือก</p>
        </section>
      </div>
    </Teleport>
  </section>
</template>

<style scoped>
.snc {
  position: relative;
  /* Home-card convention (SectionCard/TargetPacingCard/ChainWowCard):
     1rem bottom rhythm, 0.85rem padding, alias surface tokens, no shadow. */
  margin-bottom: 1rem;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-card);
  padding: 0.85rem;
  box-shadow: none;
}

/* ── Header row: canonical title + date picker ──────────────────────────── */
.snc__headrow {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.snc__headrow .card-head {
  flex: 1 1 auto;
  min-width: 0;
}

.snc__datepick-btn {
  flex-shrink: 0;
  margin-top: 0.1rem;
  min-height: 2rem;
  padding: 0.25rem 0.75rem;
  background: var(--surface-inset);
  border: 1px solid var(--border-card);
  border-radius: var(--radius-btn);
  color: var(--color-brand-red);
  font: 600 0.75rem var(--font-display);
  cursor: pointer;
}

/* ── Custom range dialog ────────────────────────────────────────────────── */
.snc__range-backdrop {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.snc__range {
  width: min(26rem, 100%);
  background: var(--surface-card);
  border: 1px solid var(--border-card);
  border-radius: var(--radius-card);
  padding: 1rem;
  box-shadow: 0 1rem 2.5rem rgba(0, 0, 0, 0.25);
}

.snc__range-title {
  font: 600 0.95rem var(--font-display);
  color: var(--text-main);
  margin: 0 0 0.75rem;
}

.snc__range-fields {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
}

.snc__range-field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex: 1 1 0%;
  min-width: 0;
}

.snc__range-field-label {
  font: var(--text-caption);
  color: var(--text-muted);
}

.snc__range-sep {
  font: 500 0.78rem var(--font-body);
  color: var(--text-faint);
  padding-bottom: 0.55rem;
}

.snc__range-input {
  width: 100%;
  min-height: 2.4rem;
  padding: 0.3rem 0.5rem;
  background: var(--surface-inset);
  border: 1px solid var(--border-card);
  border-radius: var(--radius-btn);
  color: var(--text-main);
  font: 500 0.82rem var(--font-body);
}

.snc__range-error {
  margin: 0.6rem 0 0;
  font: var(--text-body-sm);
  color: var(--color-bad);
}

.snc__range-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.9rem;
}

.snc__range-cancel,
.snc__range-go {
  min-height: 2.4rem;
  padding: 0.45rem 1rem;
  border-radius: var(--radius-btn);
  font: 600 0.85rem var(--font-display);
  cursor: pointer;
}

.snc__range-cancel {
  background: var(--surface-inset);
  border: 1px solid var(--border-strong);
  color: var(--text-body);
}

.snc__range-go {
  background: var(--color-brand-red);
  border: 1px solid var(--color-brand-red);
  color: #fff;
}

.snc__range-hint {
  margin: 0.75rem 0 0;
  font: var(--text-caption);
  color: var(--text-faint);
}

.snc__state {
  text-align: center;
  padding: 1.2rem 0;
  font: var(--text-body-sm);
  color: var(--text-faint);
}

/* ── Period toggle ──────────────────────────────────────────────────────── */
/* .card-head already supplies the 0.75rem gap above the controls. */
.snc__periods {
  display: flex;
  gap: 0.375rem;
}

.snc__period {
  flex: 1 1 0%;
  min-height: 2.5rem;
  padding: 0.5rem 0.25rem;
  background: var(--surface-card);
  border: 1px solid var(--border-card);
  border-radius: var(--radius-btn);
  color: var(--text-body);
  font: 600 0.82rem var(--font-display);
  cursor: pointer;
}

.snc__period--on {
  background: var(--surface-red-soft);
  border-color: var(--color-brand-red);
  color: var(--color-brand-red);
}

/* ── Branch selector ────────────────────────────────────────────────────── */
.snc__branch {
  position: relative;
  margin-top: 0.5rem;
}

.snc__branch-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  min-height: 2.75rem;
  padding: 0.6rem 0.9rem;
  background: var(--surface-inset);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-btn);
  color: var(--text-main);
  font: 600 0.88rem var(--font-display);
  cursor: pointer;
}

.snc__branch-caret {
  margin-left: auto;
  color: var(--color-brand-red);
  font-size: 0.8rem;
}

.snc__branch-panel {
  position: absolute;
  z-index: 30;
  left: 0;
  right: 0;
  top: calc(100% + 0.375rem);
  background: var(--surface-card);
  border: 1px solid var(--border-card);
  border-radius: var(--radius-btn);
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.12);
  padding: 0.5rem;
  max-height: 17rem;
  overflow-y: auto;
}

.snc__branch-search {
  width: 100%;
  min-height: 2.25rem;
  padding: 0.35rem 0.6rem;
  background: var(--surface-inset);
  border: 1px solid var(--border-card);
  border-radius: var(--radius-btn);
  color: var(--text-main);
  font: var(--text-body-sm);
}

.snc__branch-note {
  font: var(--text-caption);
  color: var(--text-faint);
  padding: 0.5rem 0.25rem;
  margin: 0;
}

.snc__branch-list {
  margin: 0.25rem 0 0;
  max-height: 12rem;
  overflow-y: auto;
}

.snc__branch-option {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.6rem;
  background: transparent;
  border: none;
  border-radius: var(--radius-btn);
  color: var(--text-body);
  font: 500 0.82rem var(--font-body);
  cursor: pointer;
}

.snc__branch-option--on {
  background: var(--surface-red-soft);
  color: var(--color-brand-red);
  font-weight: 600;
}

/* ── Hero ───────────────────────────────────────────────────────────────── */
.snc__hero {
  font: var(--text-hero);
  color: var(--text-main);
  margin-top: 0.8rem;
}

.snc__sub {
  font: var(--text-body-sm);
  color: var(--text-muted);
  margin-top: 0.15rem;
}

/* ── Brand split ────────────────────────────────────────────────────────── */
.snc__split {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.snc__split-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: var(--radius-btn);
  padding: 0.7rem 0.75rem;
}

.snc__split-row--suki { background: var(--suki-soft); }
.snc__split-row--bbq  { background: var(--bbq-soft); }

.snc__split-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  flex-shrink: 0;
}

.snc__split-dot--suki { background: var(--suki); }
.snc__split-dot--bbq  { background: var(--bbq); }

.snc__split-name {
  font: 500 0.85rem var(--font-display);
  color: var(--text-body);
}

.snc__split-value {
  margin-left: auto;
  font: 700 1.05rem var(--font-display);
  color: var(--text-main);
}

.snc__split-note {
  font: var(--text-caption);
  color: var(--text-faint);
  margin: 0;
}

.snc__state--chart {
  padding: 0.6rem 0;
}

/* ── Period-matched chart ───────────────────────────────────────────────── */
.snc__bars {
  display: flex;
  align-items: flex-end;
  gap: 0.375rem;
  margin-top: 0.8rem;
}

.snc__bar-col {
  flex: 1 1 0%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  min-width: 0;
}

.snc__bar-value {
  font: var(--text-caption);
  color: var(--text-body);
  white-space: nowrap;
}

.snc__bar-value--latest {
  font-weight: 700;
  color: var(--text-main);
}

.snc__bar {
  width: 100%;
  border-radius: 0.2rem 0.2rem 0 0;
  background: var(--sales);
}

.snc__bar--latest {
  background: var(--sales-light);
}

/* Past gaps: small dashed stub — never a full-height empty bar. */
.snc__bar--empty {
  background: var(--surface-inset);
  border: 1px dashed var(--border-card);
  box-sizing: border-box;
}

.snc__bar-label {
  font: var(--text-caption);
  color: var(--text-muted);
  white-space: nowrap;
}

.snc__bars-unit {
  margin: 0.3rem 0 0;
  font: var(--text-caption);
  color: var(--text-faint);
}

/* ── Detail link + footer ───────────────────────────────────────────────── */
.snc__detail {
  display: inline-block;
  margin-top: 0.75rem;
  font: 600 0.85rem var(--font-display);
  color: var(--color-brand-red);
  padding: 0.35rem 0;
}

.snc__foot {
  margin: 0.5rem 0 0;
  font: var(--text-caption);
  color: var(--text-faint);
}
</style>
